/**
 * Blume staff roles - the console's half of the staff registry.
 *
 * Pure TS: no Angular, no Firebase. blume-admin has no page specs, so every
 * rule that decides whether an action is ALLOWED lives here rather than in a
 * component, or it could not be tested at all.
 *
 * ## What this file is not
 *
 * It is not authority. The registry is unreadable and unwritable from the
 * client (`blumeStaff` is `allow read, write: if false`), and every mutation
 * goes through a Callable that re-checks the caller's roles against the live
 * doc. `canRevoke`/`canSetRoles`/`canDelete` here exist to disable a button
 * and explain why BEFORE a round trip - the server refuses the same cases
 * independently, and
 * it is the server's refusal that counts.
 *
 * Keep {@link STAFF_ROLES} in step with blume-tools
 * functions/src/lib/staff-roles.ts - same lockstep convention as the schema
 * copies in ./schema.
 */

/** Every role a Blume staff member can hold. */
export const STAFF_ROLES = ['super_admin', 'developer', 'revenue', 'support'] as const;

export type StaffRole = (typeof STAFF_ROLES)[number];

/**
 * The roles that are not `super_admin`, in display order.
 *
 * Super admin is not one item in this list, and the console must not present
 * it as one: super_admin is granted every capability there is, by identity
 * with CAPABILITIES - so it is not one area of access, it is all of them.
 * Shown as a fourth checkbox beside the others it reads as one more thing
 * someone can do.
 */
export const ADDITIONAL_ROLES: readonly StaffRole[] = STAFF_ROLES.filter(
  (role) => role !== 'super_admin',
);

/**
 * Whether the checkbox for `role` should read as ticked, given the role set
 * being edited.
 *
 * DISPLAY ONLY. A super admin shows every additional role ticked because that
 * is the truth about what they can do - but nothing writes those roles into
 * the set. The stored array is unchanged, so unticking super admin reveals
 * whatever they actually held underneath rather than the four boxes the screen
 * was showing.
 */
export function showsAsGranted(roles: readonly StaffRole[], role: StaffRole): boolean {
  return roles.includes('super_admin') || roles.includes(role);
}

/** Staff addresses are limited to this domain - a guard rail on who can be
 *  added, NOT authority. An address alone grants nothing. */
export const STAFF_DOMAIN = '@blumemethod.com';

// ============================== CAPABILITIES ==============================

/**
 * What the console can DO, named one capability at a time.
 *
 * Access is DENY BY DEFAULT. Every surface and every mutation names exactly one
 * capability; each role grants a SET of them; a person's access is the UNION of
 * their roles' sets. There is no deny list at check time, no precedence and no
 * ordering - `can()` only ever asks "is this capability in your set?".
 *
 * That matters because "all except X" does not compose. Give someone two roles,
 * one granting and one denying, and nothing says which wins. A union has no
 * such question.
 *
 * ## This table is the policy, and it is duplicated
 *
 * blume-tools functions/src/lib/staff-roles.ts and blume-admin
 * src/app/data/staff-roles.ts hold identical copies, same lockstep convention
 * as firestore.rules. That is deliberate: the server ENFORCES with it and the
 * console DRAWS with it, so a surface the console offers is a surface the
 * Callables accept. Change one, change the other, or the UI starts promising
 * things the server refuses.
 *
 * The console's use is still only presentation. The Callables re-read the
 * registry doc on every call and refuse independently; if the two ever
 * disagree, the server is right.
 */

/** What can be READ, by resource. One question each: "can this person see
 *  partners?" - not "can they see the Partners tab", which stops being the
 *  same question the moment partners appear on a second screen. */
export const READ_CAPABILITIES = [
  'partners.read',
  'licenses.read',
  'accounts.read',
  'invites.read',
  'memberships.read',
  /** Support requests - the triage inbox. */
  'support.read',
  'activity.read',
  /** The staff registry: who has console access. */
  'staff.read',
  /**
   * A district's student records (the Students tab and the student report).
   * Super only, like firestore.rules, where only `isSuper()` reads students,
   * goals and observations.
   */
  'students.read',
] as const;

/** What can be CHANGED, by resource. */
export const WRITE_CAPABILITIES = [
  /** Create, rename, schools, the student-lookup flag, and contacts. */
  'partners.write',
  /** Cascades through every subcollection and has no undo, so not partners.write. */
  'partners.delete',
  'licenses.write',
  /** Create, update, verify, enable/disable a sign-in. */
  'accounts.write',
  'accounts.delete',
  /** Invite, resend, revoke. */
  'invites.write',
  /** Per-partner deactivate, and removal from a partner. */
  'memberships.write',
  /** Support triage: lifecycle status, owning team, assignee, notes. */
  'support.write',
  /**
   * Designating a partner's Lead Administrator - the one person who may
   * accept or decline V3 for them. Held by EVERY staff role: naming the
   * contact who will decide is routine account work, not a super's
   * privilege, and the designation itself changes no access.
   */
  'governance.write',
  'mockdata.write',
  /** The launch dataset's bulk client-side write. Super only - see below. */
  'launch.populate',
  /** Granting and revoking console access itself. */
  'staff.manage',
] as const;

/**
 * NAVIGATION - "is this screen in their way", for screens whose data they can
 * reach anyway.
 *
 * They exist because "hide the Revenue dashboard from engineers" is a real
 * thing to want and is not expressible in a pure resource model.
 *
 * ## What the read capabilities actually enforce - read this before relying on one
 *
 * An earlier version of this comment said a nav capability "must never be the
 * only thing between someone and data" and that the fix is "the READ capability
 * behind it". That was wrong about six of the eight, and worth stating
 * precisely:
 *
 * - `activity.read` and `staff.read` are REAL boundaries. firestore.rules gives
 *   top-level /activity to `isSuper()` and locks blumeStaff to `if false`, so
 *   the data genuinely does not reach a non-super's browser.
 * - `partners.read`, `licenses.read`, `accounts.read`, `invites.read`,
 *   `memberships.read` and `support.read` are PRESENTATION. The rules grant
 *   those collections to `isStaff()` - any staff role - so withholding one
 *   hides a tab and nothing more; the holder can still read every document
 *   straight from the SDK.
 *
 * That is the deliberate decision recorded when this model was built (reads
 * open to all staff, only writes differ per role), not an oversight. But it
 * means a read capability is NOT a data boundary unless the rules say so
 * independently. Do not hide something sensitive behind one and assume it is
 * gone - check firestore.rules, and change it there if it is not.
 */
export const NAV_CAPABILITIES = [
  'nav.overview',
  'nav.calendar',
  'nav.engagement',
  'nav.revenue',
] as const;

export const CAPABILITIES = [
  ...READ_CAPABILITIES,
  ...WRITE_CAPABILITIES,
  ...NAV_CAPABILITIES,
] as const;

export type Capability = (typeof CAPABILITIES)[number];

/**
 * Super admin, and nobody else - whatever else a role is granted.
 *
 * Two different reasons live here, and both are about seeing rather than doing:
 *
 * - `staff.read` / `staff.manage` - who has console access, and changing it.
 * - `activity.read` - the admin activity feed. The Admin SDK writes EVERY
 *   admin's actions to it, so it is a record of colleagues' work; being able
 *   to act is not a reason to be able to watch. firestore.rules agrees
 *   independently: top-level /activity is `read: isSuper(), write: false`, so
 *   only the SDK writes it and only a super can read it.
 *
 * One list rather than a per-role exclusion, so a capability added here is
 * withheld from every role at once.
 */
const SUPER_ONLY: readonly Capability[] = [
  'staff.read',
  'staff.manage',
  'activity.read',
  'students.read',
];

/** Every read a non-super may have, plus every screen - the floor all three
 *  non-super roles stand on. Only WRITE capabilities differ between them. */
const STAFF_BASELINE: readonly Capability[] = [
  ...READ_CAPABILITIES.filter((capability) => !SUPER_ONLY.includes(capability)),
  ...NAV_CAPABILITIES,
];

/**
 * Everything except {@link SUPER_ONLY}.
 *
 * Derived by exclusion on purpose, and this is NOT the "all except X" the model
 * avoids: the exclusion is applied ONCE here, to build a plain set, and `can()`
 * still only asks whether a capability is in it. Typing these grants out by
 * hand would mean a capability added later is silently withheld, which
 * contradicts the stated policy - they get everything but those.
 *
 * `launch.populate` used to be excluded as well, for a mechanical reason rather
 * than a policy one: it was a bulk CLIENT-side write and firestore.rules gives
 * those paths to isSuper() alone, so granting it would only have drawn a button
 * that always failed. It moved behind blumeAdmin_PopulateLaunchDataset, so the
 * carve-out went with it.
 */
const FULL_ACCESS: ReadonlySet<Capability> = new Set(
  CAPABILITIES.filter((capability) => !SUPER_ONLY.includes(capability)),
);

/**
 * Role -> capabilities. THE policy table.
 *
 * `super_admin` is `CAPABILITIES` by identity, never a hand-written list: a
 * capability added above is a capability supers hold, with no second edit and
 * no chance of forgetting.
 */
export const ROLE_CAPABILITIES: Readonly<Record<StaffRole, ReadonlySet<Capability>>> = {
  super_admin: new Set(CAPABILITIES),
  // Developer and Revenue are deliberately IDENTICAL today: everything except
  // the staff registry. They are kept as separate roles because they answer
  // different questions - "who is an engineer" and "who owns commercials" -
  // and are expected to diverge. If they are still identical when someone next
  // reads this, one of them is redundant and should be said out loud rather
  // than left as an accident.
  developer: FULL_ACCESS,
  revenue: FULL_ACCESS,
  // The baseline plus the two writes that are everyone's job: support triage
  // (status, assignment, notes) and designating a partner's Lead
  // Administrator. Both granted BY NAME - every other write stays away from
  // them by construction, and would have to be added here, out loud.
  support: new Set<Capability>([...STAFF_BASELINE, 'support.write', 'governance.write']),
};

/** Everything these roles grant, together. The union - order is irrelevant. */
export function capabilitiesFor(roles: readonly StaffRole[]): ReadonlySet<Capability> {
  const granted = new Set<Capability>();
  for (const role of roles) {
    const fromRole = ROLE_CAPABILITIES[role];
    if (!fromRole) continue; // an unknown role grants nothing
    for (const capability of fromRole) granted.add(capability);
  }
  return granted;
}

/**
 * Does this role set grant this capability?
 *
 * No roles grants nothing - deny by default falls out of the loop rather than
 * needing a rule of its own.
 */
export function can(roles: readonly StaffRole[], capability: Capability): boolean {
  for (const role of roles) {
    if (ROLE_CAPABILITIES[role]?.has(capability)) return true;
  }
  return false;
}

const ROLE_LABELS: Record<StaffRole, string> = {
  super_admin: 'Super admin',
  developer: 'Developer',
  revenue: 'Revenue',
  support: 'Support',
};

/** What each role is actually for - shown beside the checkbox, because
 *  "Revenue" alone does not tell you what it unlocks. */
export const ROLE_DESCRIPTIONS: Record<StaffRole, string> = {
  super_admin: 'Everything, including granting and revoking access. Grant sparingly.',
  // Was "Seed and delete demo data" - true when `developer` only unlocked the
  // seeder, and badly wrong afterwards. This text sits beside the checkbox in
  // the Admins dialog, so it is what someone reads while deciding to grant the
  // role: it has to say what the role actually does, which is nearly
  // everything. Check it against ROLE_CAPABILITIES when that table changes.
  developer:
    'Everything except granting access, student records and the activity log. Grant sparingly.',
  revenue:
    'Everything except granting access, student records and the activity log. Grant sparingly.',
  support:
    'Support triage and Lead Administrator designation, read-only elsewhere. Cannot see who has admin access, student records, or the activity log.',
};

export function roleLabel(role: string): string {
  return ROLE_LABELS[role as StaffRole] ?? role;
}

export function isStaffRole(value: unknown): value is StaffRole {
  return typeof value === 'string' && (STAFF_ROLES as readonly string[]).includes(value);
}

/** Sorted, de-duplicated, unknown values dropped - an unrecognised role from
 *  a hand-edited doc must not render as a mystery chip. */
export function canonicalRoles(raw: unknown): StaffRole[] {
  if (!Array.isArray(raw)) return [];
  return Array.from(new Set(raw.filter(isStaffRole))).sort();
}

export function isStaffEmail(email: string): boolean {
  return email.trim().toLowerCase().endsWith(STAFF_DOMAIN);
}

/** One row of the Admins table, as the list Callable returns it. */
export interface StaffRow {
  readonly uid: string;
  /** The address on the registry doc. */
  readonly email: string;
  /** The address on the Auth record - may differ if someone changed it. */
  readonly authEmail?: string;
  readonly roles: readonly StaffRole[];
  readonly active: boolean;
  readonly grantedAt?: number;
  readonly grantedBy?: string;
  readonly revokedAt?: number;
  readonly revokedBy?: string;
  /** False when the Auth claims do not match what the doc says they should be. */
  readonly claimInSync: boolean;
  /** Their Firebase sign-in is disabled. */
  readonly disabled: boolean;
  /** Partners their Auth account belongs to. Only read by {@link deleteReach}:
   *  deleting an admin deletes that account, so this says whether the delete
   *  reaches past the console. Undefined when there is no Auth record. */
  readonly partnerCount?: number;
}

/** Why an action is refused - `null` when it is allowed. */
export type StaffGuard = string | null;

/** Active admins holding `super_admin`. Legacy docs with no roles counted as
 *  supers upstream, so by the time a row reaches here its roles are explicit. */
export function activeSupers(admins: readonly StaffRow[]): StaffRow[] {
  return admins.filter((row) => row.active && row.roles.includes('super_admin'));
}

/**
 * May this admin be revoked?
 *
 * Two refusals, both of which the Callable enforces independently:
 * - **Self.** The cost of refusing is a message to a colleague; the cost of
 *   allowing is locking yourself out of the tool that would let you back in.
 * - **The last super.** Zero supers means nobody can administer anything, and
 *   the only way back is an owner-run script against production.
 */
export function canRevoke(
  admins: readonly StaffRow[],
  target: StaffRow,
  callerUid: string,
): StaffGuard {
  if (target.uid === callerUid) return 'You cannot revoke your own access.';
  if (!target.active) return 'This admin is already revoked.';

  const supers = activeSupers(admins);
  if (target.roles.includes('super_admin') && supers.length <= 1) {
    return 'This is the last super admin. Promote someone else first.';
  }
  return null;
}

/**
 * Whether this admin can be DELETED - the registry doc and their Firebase
 * sign-in, both gone for good.
 *
 * Deliberately NOT `canRevoke` plus extra conditions, and the difference is
 * the `active` check: revoke refuses an already-revoked admin because there is
 * nothing left to revoke, while deleting one is the main thing this is for -
 * clearing out a row that has been dead for months. Reusing canRevoke here
 * would have quietly made revoked admins undeletable, which is the opposite of
 * what delete is for.
 *
 * The last-super rule still applies to a REVOKED super: they hold no authority
 * today, but until someone else is promoted they are the record of who to
 * restore, and the server refuses this case too.
 */
export function canDelete(
  admins: readonly StaffRow[],
  target: StaffRow,
  callerUid: string,
): StaffGuard {
  if (target.uid === callerUid) return 'You cannot delete your own access.';

  const supers = activeSupers(admins);
  if (target.roles.includes('super_admin') && supers.length <= 1 && target.active) {
    return 'This is the last super admin. Promote someone else first.';
  }
  return null;
}

/**
 * What deleting this admin destroys beyond console access, or null.
 *
 * `partnerCount` comes from their `partnerIds` claim: their Auth user is their
 * whole Blume identity, so deleting it removes them from every partner they
 * belong to as well. Worth saying out loud in the confirmation - it is the one
 * consequence nobody expects from a button on the Admins page.
 */
export function deleteReach(target: StaffRow): string | null {
  const count = target.partnerCount ?? 0;
  if (count <= 0) return null;
  return count === 1
    ? 'They are also a member of 1 partner, and will lose access to it.'
    : `They are also a member of ${count} partners, and will lose access to them.`;
}

/**
 * May this admin's roles be changed to `next`?
 *
 * Same two refusals, but the last-super one only bites when the change
 * actually REMOVES super - re-saving the last super with their roles intact is
 * fine, and refusing it would be baffling.
 */
export function canSetRoles(
  admins: readonly StaffRow[],
  target: StaffRow,
  next: readonly StaffRole[],
  callerUid: string,
): StaffGuard {
  if (next.length === 0) return 'Pick at least one role, or revoke their access instead.';
  if (target.uid === callerUid) return 'You cannot change your own roles.';

  const losingSuper = target.roles.includes('super_admin') && !next.includes('super_admin');
  if (losingSuper && activeSupers(admins).length <= 1) {
    return 'This is the last super admin. Promote someone else first.';
  }
  return null;
}

/** What is wrong with this row, if anything - drives the warning chip. */
export function staffWarning(row: StaffRow): string | null {
  if (!row.active) return null; // a revoked row is not "wrong"
  if (row.disabled) return 'Sign-in disabled';
  if (row.authEmail && row.authEmail.toLowerCase() !== row.email.toLowerCase()) {
    return 'Address changed';
  }
  if (!row.claimInSync) return 'Claims out of sync';
  return null;
}

/** Active first, then super admins, then by address - the order you want when
 *  scanning for who can do what. */
export function sortStaff(admins: readonly StaffRow[]): StaffRow[] {
  return [...admins].sort(
    (a, b) =>
      Number(b.active) - Number(a.active) ||
      Number(b.roles.includes('super_admin')) - Number(a.roles.includes('super_admin')) ||
      a.email.localeCompare(b.email),
  );
}
